package diana.soleil.design.iraniancanadienbazzar.di

import diana.soleil.design.iraniancanadienbazzar.core.data.di.appStateModule
import diana.soleil.design.iraniancanadienbazzar.core.data.di.coreDataModule
import diana.soleil.design.iraniancanadienbazzar.core.data.di.firestoreDataModule
import diana.soleil.design.iraniancanadienbazzar.core.data.di.mockDataModule
import diana.soleil.design.iraniancanadienbazzar.auth.authModule
import diana.soleil.design.iraniancanadienbazzar.core.common.AppBuildInfo
import diana.soleil.design.iraniancanadienbazzar.core.common.ShareService
import diana.soleil.design.iraniancanadienbazzar.core.data.ConnectivityController
import diana.soleil.design.iraniancanadienbazzar.core.data.ImagePickController
import diana.soleil.design.iraniancanadienbazzar.core.data.LocationController
import kotlinx.coroutines.flow.Flow
import diana.soleil.design.iraniancanadienbazzar.core.model.GeoPoint
import diana.soleil.design.iraniancanadienbazzar.core.model.PickedImage
import diana.soleil.design.iraniancanadienbazzar.core.devmenu.devMenuModule
import diana.soleil.design.iraniancanadienbazzar.core.firebase.AppleIdCredential
import diana.soleil.design.iraniancanadienbazzar.core.firebase.AppleSignInController
import diana.soleil.design.iraniancanadienbazzar.core.firebase.GoogleSignInController
import diana.soleil.design.iraniancanadienbazzar.core.firebase.coreFirebaseModule
import diana.soleil.design.iraniancanadienbazzar.core.firebase.firebaseTelemetryModule
import diana.soleil.design.iraniancanadienbazzar.core.firebase.noOpTelemetryModule
import diana.soleil.design.iraniancanadienbazzar.feature.addbusiness.addBusinessModule
import diana.soleil.design.iraniancanadienbazzar.feature.admin.adminModule
import diana.soleil.design.iraniancanadienbazzar.feature.chat.chatModule
import diana.soleil.design.iraniancanadienbazzar.feature.dashboard.dashboardModule
import diana.soleil.design.iraniancanadienbazzar.feature.feed.feedModule
import diana.soleil.design.iraniancanadienbazzar.feature.gems.gemsModule
import diana.soleil.design.iraniancanadienbazzar.feature.orders.ordersModule
import diana.soleil.design.iraniancanadienbazzar.feature.marketplace.marketplaceModule
import diana.soleil.design.iraniancanadienbazzar.feature.events.eventsModule
import diana.soleil.design.iraniancanadienbazzar.feature.business.businessModule
import diana.soleil.design.iraniancanadienbazzar.feature.categories.categoriesModule
import diana.soleil.design.iraniancanadienbazzar.feature.home.homeModule
import diana.soleil.design.iraniancanadienbazzar.feature.profile.profileModule
import diana.soleil.design.iraniancanadienbazzar.locale.appearanceModule
import diana.soleil.design.iraniancanadienbazzar.locale.localeModule
import diana.soleil.design.iraniancanadienbazzar.notifications.notificationsModule
import org.koin.core.module.Module
import org.koin.dsl.module

/**
 * All Koin modules for the app composition root. Core layers first, then features. The repository
 * binding is chosen by [useFirestore]: live Cloud Firestore when Firebase is initialized for the
 * platform (currently web), else the in-memory fakes. Each new feature adds its `xxxModule` here.
 *
 * [buildInfo] is the platform's real build metadata (version/env/debug), provided as a singleton so the
 * Developer Menu and the debug-gated dev-menu entry point can read it.
 */
fun appModules(
    useFirestore: Boolean,
    buildInfo: AppBuildInfo,
    shareText: (String) -> Unit = {},
    getUserLocation: suspend () -> GeoPoint? = { null },
    pickImage: suspend () -> PickedImage? = { null },
    googleIdToken: (suspend () -> String?)? = null,
    appleCredential: (suspend () -> AppleIdCredential?)? = null,
    connectivity: Flow<Boolean>? = null,
    mockData: Boolean = false,
    openUrl: (String) -> Unit = {},
): List<Module> = listOf(
    module {
        single { buildInfo }
        single { ShareService(shareText, openUrl) }
        single { LocationController(getUserLocation) }
        single { ImagePickController(pickImage) }
        single { GoogleSignInController(googleIdToken) }
        single { AppleSignInController(appleCredential) }
        single { ConnectivityController(connectivity) }
    },
    coreFirebaseModule,
    // Mock-data mode (debug Developer Menu) wins: seeded in-memory fakes, zero backend traffic.
    when {
        mockData -> mockDataModule
        useFirestore -> firestoreDataModule
        else -> coreDataModule
    },
    if (useFirestore && !mockData) firebaseTelemetryModule else noOpTelemetryModule,
    appStateModule,
    devMenuModule,
    localeModule,
    appearanceModule,
    notificationsModule,
    authModule,
    homeModule,
    profileModule,
    categoriesModule,
    businessModule,
    addBusinessModule,
    adminModule,
    chatModule,
    dashboardModule,
    feedModule,
    gemsModule,
    marketplaceModule,
    eventsModule,
    ordersModule,
)
